// A to-do as stored in `todos/{id}` (Models.swift: Todo, ChecklistItem, PlaceReminder).

import { newID } from "../ids";
import type { Day, RepeatRule, Start } from "../types";
import { encodeRepeatRule, encodeStart, readRecord, readRepeatRule, readStart } from "./shared";
import {
  decoding, document, readArray, readStruct, storedFields, withoutUndefined,
  type DecodeContext, type Decoded, type EncodeOptions, type Fields, type ValueReader,
} from "./values";
import { storedDay } from "../days";

export interface ChecklistItem {
  id: string;
  title: string;
  isDone: boolean;
}

/** A reminder on arriving at a place or leaving it. Set on the iPhone; the web keeps it as it is. */
export interface PlaceReminder {
  name: string;
  latitude: number;
  longitude: number;
  /** Metres. */
  radius: number;
  onArrival: boolean;
}

export interface Todo {
  id: string;
  title: string;
  notes: string;
  start: Start;
  /** A day, like `start`'s and the repeat's end; the other dates of a to-do are moments. */
  deadline?: Day;
  reminder?: Date;
  place?: PlaceReminder;
  repeats?: RepeatRule;
  /** Set once the to-do was added to the calendar as an event (on an Apple device). */
  calendarEventID?: string;
  tags: string[];
  checklist: ChecklistItem[];
  projectID?: string;
  /** The section inside the project: the heading in a list, the column on a board. */
  sectionID?: string;
  /** The section in Today, independent of where the to-do lives. */
  todaySectionID?: string;
  areaID?: string;
  order: number;
  createdAt: Date;
  completedAt?: Date;
  /** When the to-do left the Inbox: decided, scheduled or filed (the Inbox's history). */
  leftInboxAt?: Date;
  /** For a repeating to-do: the reminder of the series, which every next one gets. */
  seriesReminder?: Date;
  /**
   * The zone `reminder` was set in ("Europe/Berlin"): its hour and minute are read there and ring at that time wherever
   * the device is (`reminderClock`, docs/48). Noted on every save that changes the reminder; absent in older data.
   */
  reminderZone?: string;
  /** The same for `seriesReminder`. */
  seriesReminderZone?: string;
  /** Made by Todolix as an example. */
  isSample?: boolean;
  /** The Day view's plan: the end of the free time it is planned for. */
  plannedUntil?: Date;
  planOrder?: number;
  /** Who completed it in a share: their uid. */
  completedBy?: string;
  /** For the next copy of a repeating to-do: the occurrence whose completion made it. */
  spawnedFrom?: string;
  /** When it was kept in the Stale review. */
  keptAt?: Date;
  /** The habit this to-do is one day of (docs/63): its completion counts for that day. */
  habitID?: string;
  /** Top-level fields of the stored document this version doesn't know, written back unchanged. */
  unknownFields: Fields;
}

/** The fields of a to-do, in the order Models.swift declares them. */
export const TODO_FIELDS = [
  "id", "title", "notes", "start", "deadline", "reminder", "place", "repeats", "calendarEventID", "tags", "checklist",
  "projectID", "sectionID", "todaySectionID", "areaID", "order", "createdAt", "completedAt", "leftInboxAt",
  "seriesReminder", "reminderZone", "seriesReminderZone", "isSample", "plannedUntil", "planOrder", "completedBy",
  "spawnedFrom", "keptAt", "habitID",
] as const;

/** A new to-do with the defaults of Swift's `Todo()`: a new ID, in the Inbox, made now. */
export function makeTodo(values: Partial<Todo> = {}): Todo {
  return {
    id: newID(), title: "", notes: "", start: { kind: "inbox" }, tags: [], checklist: [], order: 0, createdAt: new Date(),
    unknownFields: {}, ...values,
  };
}

const readChecklistItem: ValueReader<ChecklistItem> = readStruct((fields) => ({
  id: fields.uuid("id"), title: fields.string("title"), isDone: fields.bool("isDone"),
}));

export const readPlaceReminder: ValueReader<PlaceReminder> = readStruct((fields) => ({
  name: fields.string("name"),
  latitude: fields.number("latitude"),
  longitude: fields.number("longitude"),
  radius: fields.number("radius"),
  onArrival: fields.bool("onArrival"),
}));

export function decodeTodo(data: Fields, context: DecodeContext): Decoded<Todo> {
  return decoding(() => readRecord(data, context, (fields) => {
    const todo: Todo = {
      id: fields.uuid("id"),
      title: fields.string("title"),
      notes: fields.string("notes"),
      start: fields.required("start", readStart),
      deadline: fields.optionalDay("deadline"),
      reminder: fields.optionalMoment("reminder"),
      place: fields.optional("place", readPlaceReminder),
      repeats: fields.optional("repeats", readRepeatRule),
      calendarEventID: fields.optionalString("calendarEventID"),
      tags: fields.strings("tags"),
      checklist: fields.required("checklist", readArray(readChecklistItem)),
      projectID: fields.optionalUUID("projectID"),
      sectionID: fields.optionalUUID("sectionID"),
      todaySectionID: fields.optionalUUID("todaySectionID"),
      areaID: fields.optionalUUID("areaID"),
      order: fields.number("order"),
      createdAt: fields.moment("createdAt"),
      completedAt: fields.optionalMoment("completedAt"),
      leftInboxAt: fields.optionalMoment("leftInboxAt"),
      seriesReminder: fields.optionalMoment("seriesReminder"),
      reminderZone: fields.optionalString("reminderZone"),
      seriesReminderZone: fields.optionalString("seriesReminderZone"),
      isSample: fields.optionalBool("isSample"),
      plannedUntil: fields.optionalMoment("plannedUntil"),
      planOrder: fields.optionalNumber("planOrder"),
      completedBy: fields.optionalString("completedBy"),
      spawnedFrom: fields.optionalUUID("spawnedFrom"),
      keptAt: fields.optionalMoment("keptAt"),
      habitID: fields.optionalUUID("habitID"),
      unknownFields: fields.unknownFields(TODO_FIELDS),
    };
    return withoutUndefined(todo);
  }));
}

/** The to-do's document, as Swift's encoder writes it: absent optionals left out, days as noon UTC. */
export function encodeTodo(todo: Todo, options?: EncodeOptions): Fields {
  return storedFields(document(todo.unknownFields, {
    id: todo.id,
    title: todo.title,
    notes: todo.notes,
    start: encodeStart(todo.start),
    deadline: todo.deadline === undefined ? undefined : storedDay(todo.deadline),
    reminder: todo.reminder,
    place: todo.place && { ...todo.place },
    repeats: todo.repeats && encodeRepeatRule(todo.repeats),
    calendarEventID: todo.calendarEventID,
    tags: [...todo.tags],
    checklist: todo.checklist.map((item) => ({ id: item.id, title: item.title, isDone: item.isDone })),
    projectID: todo.projectID,
    sectionID: todo.sectionID,
    todaySectionID: todo.todaySectionID,
    areaID: todo.areaID,
    order: todo.order,
    createdAt: todo.createdAt,
    completedAt: todo.completedAt,
    leftInboxAt: todo.leftInboxAt,
    seriesReminder: todo.seriesReminder,
    reminderZone: todo.reminderZone,
    seriesReminderZone: todo.seriesReminderZone,
    isSample: todo.isSample,
    plannedUntil: todo.plannedUntil,
    planOrder: todo.planOrder,
    completedBy: todo.completedBy,
    spawnedFrom: todo.spawnedFrom,
    keptAt: todo.keptAt,
    habitID: todo.habitID,
  }), options);
}

